import React, {useState} from "react";
import * as yup from "yup";
import {ErrorMessage, Field, Form, Formik} from "formik";
import {TextField} from "@material-ui/core";
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Button,
    IconButton,
} from "@mui/material";
import {
    Close as CloseIcon,
    CreditCard as CreditCardIcon,
    LogoutOutlined,
} from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import SendIcon from "@mui/icons-material/Send";
import Slide from "@mui/material/Slide";
import {paymentsApi, getUserData} from "../../services/api";
import {showSnackbar} from "../SnackBar/SnackBar";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const SubscriptionModal = ({open, onClose, subscriptionAmount, expired, expiryDate}) => {
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);

    const initialValues = {
        phone_number: "",
    };

    const validationSchema = yup.object().shape({
        phone_number: yup.string().phone("KE", true).required("Required"),
    });

    const processPayment = async (values) => {
        setPaymentLoading(true);

        let phoneNumber = values.phone_number.toString();
        if (phoneNumber.startsWith("0")) {
            phoneNumber = "254" + phoneNumber.substring(1);
        } else if (phoneNumber.startsWith("1") || phoneNumber.startsWith("7")) {
            phoneNumber = "254" + phoneNumber;
        }

        const userData = getUserData();
        const landlord_id = userData?.[0]?.[1] || "";

        const body = {
            BusinessShortCode: "6122464",
            Password: "",
            Timestamp: "",
            TransactionType: "CustomerBuyGoodsOnline",
            Amount: subscriptionAmount,
            PartyA: phoneNumber,
            PartyB: "5726811",
            PhoneNumber: phoneNumber,
            CallBackURL: "https://api.pegasus-pms.co.ke/api/pegasus/visionary/payments/subscription",
            AccountReference: "ITORAMA SOLUTIONS",
            TransactionDesc: "Subscription payment",
            landlord_id,
        };

        try {
            const response = await paymentsApi.mpesaStkPush(body);

            if (response.ok) {
                showSnackbar({
                    title: "Success!",
                    message: "Kindly wait for M-Pesa prompt on your phone",
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            } else {
                showSnackbar({
                    title: "Error!",
                    message: "Payment failed. Please try again.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
        } catch (error) {
            console.error(error);
            showSnackbar({
                title: "Error!",
                message: "Payment failed. Please try again.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        } finally {
            setPaymentLoading(false);
            setShowPaymentForm(false);
            if (!expired && onClose) {
                onClose();
            }
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = "/";
    };

    const handleClose = () => {
        if (!expired && onClose) {
            setShowPaymentForm(false);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={expired}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: "hidden",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background: expired
                        ? "linear-gradient(135deg, #e53935 0%, #d32f2f 100%)"
                        : "linear-gradient(135deg, #398DD2 0%, #2874a6 100%)",
                    p: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                    <CreditCardIcon sx={{color: "white", fontSize: 28}}/>
                    <Typography variant="h6" sx={{color: "white", fontWeight: 600}}>
                        {expired ? "Subscription Expired" : "Pay Subscription"}
                    </Typography>
                </Box>
                {!expired && (
                    <IconButton onClick={handleClose} sx={{color: "white"}}>
                        <CloseIcon/>
                    </IconButton>
                )}
            </Box>

            <DialogContent sx={{p: 3}}>
                {/* Expiry info */}
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: expired ? "#fff5f5" : "#f0f8ff",
                        border: expired ? "1px solid #ffcdd2" : "1px solid #bbdefb",
                        mb: 3,
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            color: expired ? "#c62828" : "#1565c0",
                            fontWeight: 500,
                        }}
                    >
                        {expired
                            ? `Your subscription expired on: ${expiryDate || "N/A"}`
                            : `Your subscription expires on: ${expiryDate || "N/A"}`}
                    </Typography>
                    {!expired && (
                        <Typography variant="body2" sx={{color: "#666", mt: 0.5}}>
                            Paying now will extend your subscription by 30 days
                        </Typography>
                    )}
                    {expired && (
                        <Typography variant="body2" sx={{color: "#555", mt: 0.5}}>
                            Renew your subscription to continue using the platform.
                        </Typography>
                    )}
                </Box>

                {/* Amount display */}
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        backgroundColor: "#fafafa",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Box>
                        <Typography variant="body2" sx={{color: "#888"}}>
                            Subscription Amount
                        </Typography>
                        <Typography variant="h5" sx={{fontWeight: 700, color: "#2c3e50"}}>
                            {subscriptionAmount?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "KES",
                            }) || "KES 0"}
                        </Typography>
                    </Box>
                    {!showPaymentForm && (
                        <Button
                            variant="contained"
                            onClick={() => setShowPaymentForm(true)}
                            sx={{
                                backgroundColor: "#398DD2",
                                textTransform: "none",
                                borderRadius: 2,
                                px: 3,
                                "&:hover": {backgroundColor: "#2874a6"},
                            }}
                        >
                            Pay Now
                        </Button>
                    )}
                </Box>

                {/* Payment form */}
                {showPaymentForm && (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            border: "1px solid #e0e0e0",
                            backgroundColor: "#fff",
                            mb: 2,
                        }}
                    >
                        <Typography variant="subtitle2" sx={{mb: 1.5, color: "#333"}}>
                            Enter M-Pesa phone number to receive payment prompt
                        </Typography>
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={processPayment}
                        >
                            {(props) => (
                                <Form noValidate>
                                    <Field
                                        as={TextField}
                                        name="phone_number"
                                        label="Phone Number"
                                        placeholder="e.g., 07XXXXXXXX"
                                        error={props.errors.phone_number && props.touched.phone_number}
                                        helperText={<ErrorMessage name="phone_number"/>}
                                        required
                                        variant="outlined"
                                        style={{width: "100%"}}
                                    />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: 1,
                                            mt: 2,
                                        }}
                                    >
                                        <Button
                                            onClick={() => setShowPaymentForm(false)}
                                            sx={{color: "#888", textTransform: "none"}}
                                        >
                                            Cancel
                                        </Button>
                                        <LoadingButton
                                            type="submit"
                                            variant="contained"
                                            loading={paymentLoading}
                                            startIcon={<SendIcon/>}
                                            sx={{
                                                backgroundColor: "#4caf50",
                                                textTransform: "none",
                                                "&:hover": {backgroundColor: "#388e3c"},
                                            }}
                                            loadingPosition="start"
                                        >
                                            Send Prompt
                                        </LoadingButton>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    </Box>
                )}

                {/* Footer note + logout for expired */}
                {expired && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 2,
                            pt: 2,
                            borderTop: "1px solid #eee",
                        }}
                    >
                        <Typography variant="body2" sx={{color: "#888", maxWidth: "60%"}}>
                            After subscribing, you'll receive a confirmation from IT-orama. Log in again to update your
                            payment details.
                        </Typography>
                        <Button
                            onClick={handleLogout}
                            variant="outlined"
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                borderColor: "#d32f2f",
                                color: "#d32f2f",
                                "&:hover": {
                                    borderColor: "#b71c1c",
                                    backgroundColor: "#ffebee",
                                },
                            }}
                            startIcon={<LogoutOutlined/>}
                        >
                            Logout
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SubscriptionModal;
